import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../shared/utils/request";
import LoginWarning from "../shared/warningPages";
import PageLoader from "../shared/pageLoader";
import AccountSidebar from "../shared/accountSidebar";

const OrderDetailsPage = () => {
  const isToken = localStorage.getItem("token");
  const { slug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (isToken && slug) {
      fetchOrderDetails();
    } else {
      setLoading(false);
    }
  }, [slug, isToken]);

  const fetchOrderDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(`customer/getOrderByCode/${slug}`);
      if (data?.status && data?.data) {
        setOrder(data.data);
      } else {
        setOrder(null);
        setError(data?.message || "Order not found");
      }
    } catch (error) {
      console.error("Error fetching order:", error);
      setOrder(null);
      setError("Failed to load order details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "warning",
      confirmed: "info",
      processing: "info",
      completed: "success",
      shipped: "primary",
      delivered: "success",
      cancelled: "danger",
    };
    return colors[status?.toLowerCase()] || "secondary";
  };

  const formatAmount = (value) => `₹${Number(value ?? 0).toFixed(2)}`;

  const getAmountBreakdown = (data = {}) => {
    const subtotal = Number(data.total_amount || 0);
    const totalGst = Number(data.cgst || 0) + Number(data.sgst || 0);
    const total = subtotal + totalGst - Number(data.discount_amount || 0);
    return { subtotal, totalGst, total };
  };

  const address = order?.customer_address;
  const deliveryBoy = order?.delivery_boy;
  // const orderBreakdown = getAmountBreakdown(order);

  const stats = [
    {
      icon: "ph-circle",
      label: "Order Status",
      value: order?.status || "—",
      chipBg: "bg-main-50",
      chipText: "text-main-600",
    },
    {
      icon: "ph-calendar-check",
      label: "Delivery Date",
      value: order?.delivery_date || "Not scheduled",
      chipBg: "bg-main-50",
      chipText: "text-main-600",
    },
    {
      icon: "ph-clock",
      label: "Time Slot",
      value: order?.delivery_slot || "Flexible",
      chipBg: "bg-main-50",
      chipText: "text-main-600",
    },
    {
      icon: "ph-credit-card",
      label: "Payment",
      value: order?.payment_type || "—",
      chipBg: "bg-main-50",
      chipText: "text-main-600",
    },
  ];

  if (!isToken && !loading) return <LoginWarning />;

  return (
    <>
      {loading && <PageLoader />}

      <section className="py-40" style={{ background: "linear-gradient(135deg, #f6faf7 0%, #edf5f0 100%)" }}>
        <div className="container container-lg">
          <div className="row g-3">
            <div className="col-lg-3 mb-16 mb-lg-0">
              <AccountSidebar />
            </div>

            <div className="col-lg-9">
              {order && (
                <>
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-8 mb-16">
                    <button
                      className="btn btn-outline-main d-inline-flex align-items-center gap-6 rounded-pill"
                      style={{ fontSize: "13px" }}
                      onClick={() => navigate("/my-orders")}
                    >
                      <i className="ph ph-arrow-left" />
                      My Orders
                    </button>

                    <div className="d-flex align-items-center gap-6 flex-wrap">
                      <span className="text-xs text-neutral-500">
                        Order #{order?.order_code}
                      </span>
                      <span className="text-neutral-300">•</span>
                      <span className="text-xs text-neutral-500 d-flex align-items-center gap-4">
                        <i className="ph ph-calendar-blank" />
                        {order?.order_date}
                        {order?.order_time ? ` • ${order?.order_time}` : ""}
                      </span>
                    </div>
                  </div>

                  {/* Status Banner */}
                  <div
                    className="rounded-12 bg-white border border-main-100 p-12 p-sm-16 mb-16"
                    style={{ boxShadow: "0 2px 8px rgba(26, 107, 48, 0.06)" }}
                  >
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-12">
                      <div className="d-flex align-items-center gap-10">
                        <div className="d-flex align-items-center justify-content-center rounded-10 bg-main-600 text-white flex-shrink-0" style={{ width: "46px", height: "46px", boxShadow: "0 4px 12px rgba(26, 107, 48, 0.3)" }}>
                          <i className="ph ph-package text-xl" />
                        </div>
                        <div>
                          <h5 className="mb-1 fw-bold text-main-700" style={{ fontSize: "1.05rem" }}>
                            #{order?.order_code}
                          </h5>
                          <p className="mb-0 text-neutral-500 text-sm">
                            Placed on {order?.order_date}
                            {order?.order_time ? ` at ${order?.order_time}` : ""}
                          </p>
                        </div>
                      </div>

                      <div className="d-flex align-items-center gap-6 flex-wrap">
                        <span
                          className={`badge bg-${getStatusColor(order?.status)} text-white px-10 py-6 rounded-pill`}
                          style={{ fontSize: "11px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}
                        >
                          <i className="ph ph-circle-fill me-4" style={{ fontSize: "7px" }} />
                          {order?.status}
                        </span>
                        <span
                          className={`badge ${order?.payment_status?.toLowerCase() === "completed" ? "bg-success" : "bg-warning"} text-white px-10 py-6 rounded-pill`}
                          style={{ fontSize: "11px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}
                        >
                          <i className="ph ph-bank me-4" />
                          {order?.payment_status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="row g-3 mb-16">
                    {stats.map((stat) => (
                      <div key={stat.label} className="col-6 col-md-3">
                        <div
                          className="rounded-12 bg-white border border-main-100 h-100 p-12"
                          style={{ boxShadow: "0 2px 8px rgba(26, 107, 48, 0.05)" }}
                        >
                          <div className={`d-flex align-items-center justify-content-center rounded-10 ${stat.chipBg} ${stat.chipText} mb-8`} style={{ width: "34px", height: "34px" }}>
                            <i className={`ph ${stat.icon}`} style={{ fontSize: "16px" }} />
                          </div>
                          <p className="mb-1 text-neutral-500 text-xs">{stat.label}</p>
                          <p className="mb-0 fw-bold text-main-700 text-truncate" style={{ fontSize: "13px" }} title={stat.value}>
                            {stat.value}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Order Items */}
                  <div className="rounded-12 bg-white border border-main-100 mb-16" style={{ boxShadow: "0 2px 8px rgba(26, 107, 48, 0.05)" }}>
                    <div className="d-flex align-items-center justify-content-between px-12 px-sm-16 py-10" style={{ borderBottom: "1px solid #eef5f0" }}>
                      <h6 className="mb-0 fw-bold text-main-700" style={{ fontSize: "0.95rem" }}>
                        <i className="ph ph-cube me-8 text-main-600" />
                        Order Items
                      </h6>
                      <span className="text-xs text-neutral-500">
                        {order?.items?.length || 0} item(s)
                      </span>
                    </div>
                    <div className="px-12 px-sm-16 py-8">
                      {order?.items?.length ? (
                        order.items.map((item, index) => {
                          const { subtotal, totalGst, total } =
                            getAmountBreakdown(item);
                          return (
                            <div
                              key={item.id ?? index}
                              className={`d-flex align-items-center gap-10 py-10 ${index !== order.items.length - 1 ? "border-bottom" : ""}`}
                              style={{ borderColor: "#eef5f0" }}
                            >
                              <div
                                className="d-flex align-items-center justify-content-center rounded-10 flex-shrink-0 bg-neutral-50 overflow-hidden"
                                style={{ width: "56px", height: "56px" }}
                              >
                                {item.variant?.product_image ? (
                                  <img
                                    src={item.variant.product_image}
                                    alt={item.variant?.title || "Product"}
                                    className="w-100 h-100"
                                    style={{ objectFit: "contain" }}
                                    onError={(e) => (e.target.style.display = "none")}
                                  />
                                ) : (
                                  <i className="ph ph-fish text-xl text-neutral-300" />
                                )}
                              </div>
                              <div className="flex-grow-1 overflow-hidden">
                                <p className="mb-1 fw-semibold text-truncate text-neutral-800" style={{ fontSize: "0.85rem" }}>
                                  {item.variant?.title || item.product?.product_name || "Product"}
                                </p>
                                <p className="mb-0 text-neutral-500 text-xs">
                                  Qty: {item.quantity}
                                  <span className="text-neutral-300 mx-4">|</span>
                                  {formatAmount(item.price)} each
                                </p>
                                <p className="mb-0 text-success" style={{ fontSize: "0.7rem" }}>
                                  <i className="ph ph-receipt me-4" />
                                  GST {Number(item.cgst_rate || 0) + Number(item.sgst_rate || 0)}%
                                </p>
                              </div>
                              <div className="text-end flex-shrink-0">
                                <p className="mb-0 fw-bold text-main-600" style={{ fontSize: "0.9rem" }}>
                                  {formatAmount(total)}
                                </p>
                                <p className="mb-0 text-neutral-400" style={{ fontSize: "0.68rem" }}>
                                  Subtotal {formatAmount(subtotal)}
                                </p>
                                <p className="mb-0 text-neutral-400" style={{ fontSize: "0.68rem" }}>
                                  GST {formatAmount(totalGst)}
                                </p>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-muted mb-0 py-8" style={{ fontSize: "0.85rem" }}>
                          No items in this order.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="row g-3 mb-16">
                    {/* Delivery Address */}
                    <div className="col-md-6">
                      <div className="rounded-12 bg-white border border-main-100 h-100" style={{ boxShadow: "0 2px 8px rgba(26, 107, 48, 0.05)" }}>
                        <div className="px-12 px-sm-16 py-10" style={{ borderBottom: "1px solid #eef5f0" }}>
                          <h6 className="mb-0 fw-bold text-main-700" style={{ fontSize: "0.95rem" }}>
                            <i className="ph ph-map-pin me-8 text-danger" />
                            Delivery Address
                          </h6>
                        </div>
                        <div className="px-12 px-sm-16 py-12">
                          {address ? (
                            <>
                              <p className="mb-1 fw-semibold text-neutral-800" style={{ fontSize: "0.85rem" }}>{address.name}</p>
                              <p className="mb-1 text-neutral-500" style={{ fontSize: "0.8rem" }}>
                                {[address.address_line_1, address.address_line_2].filter(Boolean).join(", ")}
                              </p>
                              <p className="mb-2 text-neutral-500" style={{ fontSize: "0.8rem" }}>
                                {[address.city, address.state, address.pincode].filter(Boolean).join(", ")}
                              </p>
                              {address.phone && (
                                <a
                                  href={`tel:${address.phone}`}
                                  className="d-inline-flex align-items-center gap-6 text-main-600 text-decoration-none fw-semibold"
                                  style={{ fontSize: "0.8rem" }}
                                >
                                  <i className="ph ph-phone" />
                                  {address.phone}
                                </a>
                              )}
                            </>
                          ) : (
                            <p className="text-neutral-500 mb-0" style={{ fontSize: "0.8rem" }}>
                              Address not available.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Delivery Partner */}
                    <div className="col-md-6">
                      <div className="rounded-12 bg-white border border-main-100 h-100" style={{ boxShadow: "0 2px 8px rgba(26, 107, 48, 0.05)" }}>
                        <div className="px-12 px-sm-16 py-10" style={{ borderBottom: "1px solid #eef5f0" }}>
                          <h6 className="mb-0 fw-bold text-main-700" style={{ fontSize: "0.95rem" }}>
                            <i className="ph ph-user-circle me-8 text-primary" />
                            Delivery Partner
                          </h6>
                        </div>
                        <div className="px-12 px-sm-16 py-12">
                          {deliveryBoy ? (
                            <>
                              <p className="mb-1 fw-semibold text-neutral-800" style={{ fontSize: "0.85rem" }}>{deliveryBoy.name}</p>
                              <p className="mb-1 text-neutral-500 d-flex align-items-center gap-6" style={{ fontSize: "0.8rem" }}>
                                <i className="ph ph-phone text-success" />
                                {deliveryBoy.phone}
                              </p>
                              <p className="mb-0 text-neutral-500 d-flex align-items-center gap-6" style={{ fontSize: "0.8rem" }}>
                                <i className="ph ph-motorcycle text-warning" />
                                {[deliveryBoy.vehicle_type, deliveryBoy.vehicle_number].filter(Boolean).join(" - ") || "Bike"}
                              </p>
                              {deliveryBoy.phone && (
                                <a
                                  className="btn btn-sm btn-main mt-12 d-inline-flex align-items-center gap-6 rounded-pill"
                                  style={{ fontSize: "0.75rem", padding: "0.4rem 1rem" }}
                                  href={`tel:${deliveryBoy.phone}`}
                                >
                                  <i className="ph ph-phone-call" />
                                  Call Now
                                </a>
                              )}
                            </>
                          ) : (
                            <div className="text-center py-16">
                              <i className="ph ph-user-circle text-neutral-300" style={{ fontSize: "2.5rem" }} />
                              <p className="text-neutral-500 mb-0 mt-8" style={{ fontSize: "0.8rem" }}>
                                Delivery partner not assigned yet
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payment Summary */}
                  <div className="rounded-12 bg-white border border-main-100" style={{ boxShadow: "0 2px 8px rgba(26, 107, 48, 0.05)" }}>
                    <div className="px-12 px-sm-16 py-10" style={{ borderBottom: "1px solid #eef5f0" }}>
                      <h6 className="mb-0 fw-bold text-main-700" style={{ fontSize: "0.95rem" }}>
                        <i className="ph ph-receipt me-8 text-main-600" />
                        Payment Summary
                      </h6>
                    </div>
                    <div className="px-12 px-sm-16 py-12">
                      <div className="d-flex justify-content-between mb-8">
                        <span className="text-neutral-500" style={{ fontSize: "0.8rem" }}>Subtotal</span>
                        <span className="text-neutral-800 fw-semibold" style={{ fontSize: "0.8rem" }}>{formatAmount(order?.subtotal)}</span>
                      </div>
                      {Number(order?.discount_amount) > 0 && (
                        <div className="d-flex justify-content-between mb-8">
                          <span className="text-neutral-500" style={{ fontSize: "0.8rem" }}>
                            Discount{order?.coupon ? ` (${order.coupon.code})` : ""}
                          </span>
                          <span className="text-success fw-semibold" style={{ fontSize: "0.8rem" }}>−{formatAmount(order?.discount_amount)}</span>
                        </div>
                      )}
                      <div className="d-flex justify-content-between mb-8">
                        <span className="text-neutral-500" style={{ fontSize: "0.8rem" }}>Total GST</span>
                        <span className="text-neutral-800 fw-semibold" style={{ fontSize: "0.8rem" }}>{formatAmount(order?.total_tax)}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-12" style={{ borderTop: "1px dashed #dcebe0", paddingTop: "10px" }}>
                        <strong className="text-main-700" style={{ fontSize: "0.95rem" }}>Total Amount</strong>
                        <strong className="text-main-600" style={{ fontSize: "1.05rem" }}>{formatAmount(order?.total_amount)}</strong>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-neutral-500" style={{ fontSize: "0.8rem" }}>Payment Method</span>
                        <span className="badge bg-main-50 text-main-700 px-10 py-6 rounded-pill" style={{ fontSize: "11px" }}>
                          <i className="ph ph-credit-card me-4" />
                          {order?.payment_type}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {!order && !loading && (
                <div className="rounded-12 bg-white border border-main-100 text-center px-16 py-40" style={{ boxShadow: "0 2px 8px rgba(26, 107, 48, 0.05)" }}>
                  <i className="ph ph-package text-6xl text-neutral-300 mb-16 d-block" />
                  <h5 className="fw-bold mb-8">Order not found</h5>
                  <p className="text-neutral-500 mb-24" style={{ fontSize: "0.85rem" }}>{error}</p>
                  <button
                    className="btn btn-main d-inline-flex align-items-center gap-6 rounded-pill"
                    style={{ width: "fit-content" }}
                    onClick={() => navigate("/my-orders")}
                  >
                    <i className="ph ph-arrow-left" />
                    Back to Orders
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
export default OrderDetailsPage;
